import React from "react";
import { useState, useEffect } from "react";
import Like from "../../../Images/Like.png";
import Comment from "../../../Images/Comment.png";
import {
  getCommentsByIds,
  createComment,
  sendFriendRequest,
  removeFriend,
  UserProfile,
} from "..//actions/index"; // Changed manually
import { UserProfileModal } from "./UserProfileModal"; // Changed manually
import RetrieveUserProfileById from "../../UserProfile/RetrieveUserProfileById"; // Changed manually

export interface PostInterface {
  id: string;
  content: string;
  username: string;
  userId: string;
  likedBy: string[];
  commentIds: string[];
  createdAt: Date;
  imageUrl?: string;
  videoUrl?: string;
}

export interface Comment {
  id: string;
  text: string;
  userId: string;
  postId: string;
  username: string;
  createdAt: Date;
}

interface PostProps {
  post: PostInterface;
  currentUserId: string;
  currentUsername: string;
  onLike: (postId: string) => Promise<void>;
  onUnlike: (postId: string) => Promise<void>;
}

const Post = ({
  post,
  currentUserId,
  currentUsername,
  onLike,
  onUnlike,
}: PostProps) => {
  const [showComments, setShowComments] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [comments, setComments] = useState([] as Comment[]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] =
    useState<UserProfile | null>(null);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isRemovingFriend, setIsRemovingFriend] = useState(false);
  const isLiked = post.likedBy.includes(currentUserId);

  const handleUsernameClick = async () => {
    try {
      const userProfile = await RetrieveUserProfileById(post.userId);
      if (userProfile) {
        // console.log(userProfile);
        setSelectedUserProfile(userProfile);
        setShowUserProfile(true);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const handleSendFriendRequest = async () => {
    if (isSendingRequest) return;
    setIsSendingRequest(true);
    try {
      const success = await sendFriendRequest(currentUserId, post.userId);
      if (success) {
        alert("Friend request sent successfully!");
      } else {
        alert("Failed to send friend request. Please try again.");
      }
    } catch (error) {
      console.error("Error sending friend request:", error);
      alert("Error sending friend request. Please try again.");
    } finally {
      setIsSendingRequest(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (isRemovingFriend) return;
    setIsRemovingFriend(true);
    try {
      const success = await removeFriend(currentUserId, post.userId);
      if (success) {
        alert("Friend removed successfully!");
      } else {
        alert("Failed to remove friend. Please try again.");
      }
    } catch (error) {
      console.error("Error removing friend:", error);
      alert("Error removing friend. Please try again.");
    } finally {
      setIsRemovingFriend(false);
    }
  };

  const getFriendshipStatus = () => {
    if (!selectedUserProfile) return null;
    // console.log(selectedUserProfile);
    // if (selectedUserProfile.friends?.includes(currentUserId)) {
    //   return {
    //     text: isRemovingFriend ? "Removing..." : "Remove Friend",
    //     action: handleRemoveFriend,
    //     disabled: isRemovingFriend,
    //     color: "#ef4444", // red color for remove action
    //   };
    // }

    // if (selectedUserProfile.friendRequests?.includes(currentUserId)) {
    //   return {
    //     text: "Request Pending",
    //     action: undefined,
    //     disabled: true,
    //     color: "#9ca3af", // gray color for pending
    //   };
    // }
    if (selectedUserProfile.friends?.includes(currentUserId)) {
      return null;
    }

    if (selectedUserProfile.friendRequests?.includes(currentUserId)) {
      return null;
    }

    if (currentUserId !== post.userId) {
      return {
        text: isSendingRequest ? "Sending..." : "Add Friend",
        action: handleSendFriendRequest,
        disabled: isSendingRequest,
        color: "#3b82f6", // blue color for add
      };
    }

    return null;
  };

  useEffect(() => {
    if (showComments && post.commentIds.length > 0) {
      const fetchComments = async () => {
        setIsLoadingComments(true);
        try {
          const fetchedComments = await getCommentsByIds(post.commentIds);
          setComments(fetchedComments);
        } catch (error) {
          console.error("Error fetching comments:", error);
        } finally {
          setIsLoadingComments(false);
        }
      };
      fetchComments();
    }
  }, [showComments, post.commentIds]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmittingComment) return;

    setIsSubmittingComment(true);
    try {
      await createComment(
        post.id,
        currentUserId,
        currentUsername,
        newComment.trim()
      );

      setNewComment("");

      // Refresh comments
      const updatedComments = await getCommentsByIds(post.commentIds);
      setComments(updatedComments);
      setShowComments(false);
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInHours = (now.getTime() - postDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      if (hours < 1) {
        const minutes = Math.floor(diffInHours * 60);
        return `${minutes}m`;
      }
      return `${hours}h`;
    } else {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[postDate.getMonth()];
      const day = postDate.getDate();
      return `${month} ${day}`;
    }
  };

  return (
    <article
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Modal for full-size image */}
      {showFullImage && post.imageUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            cursor: "pointer",
          }}
          onClick={() => setShowFullImage(false)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <img
              src={post.imageUrl}
              alt="Post"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      )}

      {/* Modal for comments */}
      {showComments && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowComments(false)}
        >
          <div
            style={{
              position: "relative",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: "24px" }}>
              {/* User info and timestamp */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{ display: "flex", gap: "10px", marginBottom: "16px" }}
                >
                  <p
                    style={{
                      fontSize: "0.8rem",
                      textAlign: "start",
                      cursor: "pointer",
                    }}
                    onClick={handleUsernameClick}
                  >
                    {post.username}
                  </p>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      color: "gray",
                      textAlign: "start",
                    }}
                  >
                    {formatDate(post.createdAt)}
                  </p>
                </div>
                {getFriendshipStatus() && (
                  <p
                    onClick={
                      getFriendshipStatus()?.disabled
                        ? undefined
                        : getFriendshipStatus()?.action
                    }
                    style={{
                      color: getFriendshipStatus()?.color,
                      cursor: getFriendshipStatus()?.disabled
                        ? "not-allowed"
                        : "pointer",
                      fontSize: "small",
                    }}
                  >
                    {getFriendshipStatus()?.text}
                  </p>
                )}
              </div>
              {/* Post content */}
              <p
                style={{
                  color: "#333",
                  marginBottom: "16px",
                  textAlign: "start",
                }}
              >
                {post.content}
              </p>
              {post.imageUrl && (
                <div
                  style={{
                    width: "100%",
                    marginBottom: "16px",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowFullImage(true)}
                >
                  <img
                    src={post.imageUrl}
                    alt="Post"
                    style={{
                      width: "100%",
                      maxHeight: "400px",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              )}
              {post.videoUrl && (
                <div
                  style={{
                    width: "100%",
                    marginBottom: "16px",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <video
                    controls
                    style={{
                      width: "100%",
                      maxHeight: "400px",
                      objectFit: "contain",
                      backgroundColor: "#000",
                    }}
                  >
                    <source src={post.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
              {/* Like and Comment Buttons */}
              <div
                style={{
                  marginBottom: "16px",
                  color: "#6b7280",
                  fontSize: "0.875rem",
                  justifyContent: "space-between",
                  display: "flex",
                  width: "100%",
                  textAlign: "start",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    marginRight: "8px",
                    color: isLiked ? "#1877f2" : "#b0b3b8",
                  }}
                >
                  {post.likedBy.length}{" "}
                  {post.likedBy.length === 1 ? "like" : "likes"}
                </div>
                <div
                  style={{
                    display: "inline-block",
                    marginRight: "8px",
                  }}
                >
                  {post.commentIds.length} Comment
                  {post.commentIds.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div
                style={{
                  borderTop: "1px solid #23272f",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    // justifyContent: 'space-around',
                    alignItems: "start",
                    borderRadius: "0 0 12px 12px",
                  }}
                >
                  {/* Like Button */}
                  <button
                    onClick={() =>
                      isLiked ? onUnlike(post.id) : onLike(post.id)
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: isLiked ? "#1877f2" : "#b0b3b8",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: 600,
                      fontSize: "1rem",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <img
                      src={Like}
                      alt="Like Icon"
                      width="18"
                      height="18"
                    ></img>{" "}
                    Like
                  </button>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            {/* <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <button
                onClick={() => setShowComments(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div> */}

            <form
              onSubmit={handleSubmitComment}
              style={{ marginBottom: "20px" }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: "20px",
                    border: "1px solid #e5e7eb",
                    outline: "none",
                    fontSize: "0.875rem",
                  }}
                  disabled={isSubmittingComment}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || isSubmittingComment}
                  style={{
                    padding: "8px 16px",
                    backgroundColor:
                      !newComment.trim() || isSubmittingComment
                        ? "#e5e7eb"
                        : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "20px",
                    cursor:
                      !newComment.trim() || isSubmittingComment
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "0.875rem",
                  }}
                >
                  {isSubmittingComment ? "Posting..." : "Post"}
                </button>
              </div>
            </form>

            {/* Comments list */}
            {isLoadingComments ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading comments...
              </div>
            ) : comments?.length > 0 ? (
              comments?.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    backgroundColor: "#f9fafb",
                    padding: "12px",
                    borderRadius: "8px",
                    marginBottom: "12px",
                    color: "#4b5563",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.8rem",
                        textAlign: "start",
                      }}
                    >
                      {comment.username}
                    </p>
                    <p
                      style={{
                        fontSize: "0.8rem",
                        color: "gray",
                        textAlign: "start",
                      }}
                    >
                      {formatDate(comment.createdAt)}
                    </p>
                  </div>
                  <p
                    style={{
                      color: "#333",
                      marginTop: "8px",
                      textAlign: "start",
                    }}
                  >
                    {comment.text}
                  </p>
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#6b7280",
                }}
              >
                No comments yet
              </div>
            )}
          </div>
        </div>
      )}

      {showUserProfile && selectedUserProfile && (
        <UserProfileModal
          user={selectedUserProfile}
          currentUserId={currentUserId}
          onClose={() => setShowUserProfile(false)}
        />
      )}

      <div
        style={{
          padding: "24px",
        }}
      >
        {/* User info and timestamp */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: "16px",
            }}
          >
            <p
              style={{
                fontSize: "0.8rem",
                textAlign: "start",
                cursor: "pointer",
              }}
              onClick={handleUsernameClick}
            >
              {post.username}
            </p>
            <p
              style={{
                fontSize: "0.8rem",
                color: "gray",
                textAlign: "start",
              }}
            >
              {formatDate(post.createdAt)}
            </p>
          </div>
          {getFriendshipStatus() && (
            <p
              onClick={
                getFriendshipStatus()?.disabled
                  ? undefined
                  : getFriendshipStatus()?.action
              }
              style={{
                color: getFriendshipStatus()?.color,
                cursor: getFriendshipStatus()?.disabled
                  ? "not-allowed"
                  : "pointer",
                fontSize: "small",
              }}
            >
              {getFriendshipStatus()?.text}
            </p>
          )}
        </div>

        {/* Post content */}
        <p
          style={{
            color: "#333",
            marginBottom: "16px",
            textAlign: "start",
          }}
        >
          {post.content}
        </p>

        {post.imageUrl && (
          <div
            style={{
              width: "100%",
              marginBottom: "16px",
              cursor: "pointer",
            }}
            onClick={() => setShowFullImage(true)}
          >
            <img
              // src="https://picsum.photos/200/300"
              src={post.imageUrl}
              alt="Post"
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </div>
        )}
        {/* {post && ( */}
        {post.videoUrl && (
          <div
            style={{
              width: "100%",
              marginBottom: "16px",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <video
              controls
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            >
              <source src={post.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {/* Interactions */}
        {/* {post.likedBy.length > 0 ? (
          <div
            style={{
              marginBottom: '16px',
              color: '#6b7280',
              fontSize: '0.875rem',
              width: '100%',
              textAlign: 'start',
            }}
          >
            {post.likedBy.length} {post.likedBy.length === 1 ? 'like' : 'likes'}
          </div>
        ) : (
          <div
            style={{
              marginBottom: '16px',
              color: '#6b7280',
              fontSize: '0.875rem',
              width: '100%',
              textAlign: 'start',
            }}
          >
            0 likes
          </div>
        )} */}
        <div
          style={{
            marginBottom: "16px",
            color: "#6b7280",
            fontSize: "0.875rem",
            justifyContent: "space-between",
            display: "flex",
            width: "100%",
            textAlign: "start",
          }}
        >
          <div
            style={{
              display: "inline-block",
              marginRight: "8px",
              color: isLiked ? "#1877f2" : "#b0b3b8",
            }}
          >
            {post.likedBy.length} {post.likedBy.length === 1 ? "like" : "likes"}
          </div>
          <div
            style={{
              display: "inline-block",
              marginRight: "8px",
              // color: isLiked ? '#1877f2' : '#b0b3b8',
            }}
          >
            {post.commentIds.length} Comment
            {post.commentIds.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div
          style={{
            borderTop: "1px solid #23272f",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              // background: '#18191a',
              borderRadius: "0 0 12px 12px",
            }}
          >
            {/* Like Button */}
            <button
              onClick={() => (isLiked ? onUnlike(post.id) : onLike(post.id))}
              style={{
                background: "none",
                border: "none",
                color: isLiked ? "#1877f2" : "#b0b3b8",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                fontSize: "1rem",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <img src={Like} alt="Like Icon" width="18" height="18"></img> Like
            </button>

            {/* Comment Button */}
            <button
              onClick={() => setShowComments(true)}
              style={{
                background: "none",
                border: "none",
                color: "#b0b3b8",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                fontSize: "1rem",
                cursor: "pointer",
                outline: "none",
              }}
              disabled={showComments}
            >
              <img
                src={Comment}
                alt="Comment Icon"
                width="18"
                height="18"
              ></img>{" "}
              Comment
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default Post;
